import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import HistoryIcon from "@mui/icons-material/History";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FollowTheSignsIcon from "@mui/icons-material/FollowTheSigns";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import SpeedIcon from "@mui/icons-material/Speed";
import { Link as RouterLink } from "react-router-dom";
import { differenceInWeeks, parseISO, subDays } from "date-fns";
import MiniStatCard from "../../ui/cards/MiniStatCard";
import {
  useGetSecureVolumeCustomersQuery,
  useGetSecureVolumeProductsQuery,
  usePopulateSecureVolumeWorkMutation,
} from "../../../store/services/secureVolumeService";
import { SecureVolumeProductSelect } from "../../secure-volume/SecureVolumeProductSelect";
import { findProduct, formatProductPeriod, getProductName } from "../../secure-volume/productLabels";
import { useWorkPanel } from "../../../hooks/useWorkPanel";
import { useSalesPersons } from "../../../hooks/usePersonsData";
import {
  SecureVolumeCustomerListItem,
  WorkProgressStateEnum,
} from "../../../store/types/secureVolume.types";
import { PersonOption } from "../../../types/AssignPerson.types";
import { formatSmartDate } from "../../../utils/dateFormatters";
import { useUserRoles } from "../../../hooks/useUserRoles";

// Fallback om produktlistan inte kan laddas – historiskt har kampanjen slutat 30 juni
const DEFAULT_DEADLINE = new Date(new Date().getFullYear(), 5, 30);

const progressConfig: Record<
  WorkProgressStateEnum,
  { label: string; color: "default" | "warning" | "info" | "success" | "error" }
> = {
  [WorkProgressStateEnum.NotSet]: { label: "Inget ärende", color: "default" },
  [WorkProgressStateEnum.New]: { label: "Ny", color: "default" },
  [WorkProgressStateEnum.InProgress]: { label: "Pågående", color: "warning" },
  [WorkProgressStateEnum.VolumePresented]: { label: "Volym presenterad", color: "info" },
  [WorkProgressStateEnum.ContractCreated]: { label: "Avtal skapat", color: "warning" },
  [WorkProgressStateEnum.ContractSent]: { label: "Avtal skickat", color: "warning" },
  [WorkProgressStateEnum.ContractSigned]: { label: "Avtal signerat", color: "success" },
  [WorkProgressStateEnum.Completed]: { label: "Klarmarkerat", color: "success" },
  [WorkProgressStateEnum.Submitted]: { label: "Inskickad till Zynergy", color: "success" },
};

interface UserSummary {
  id: number;
  name: string;
  avatar?: string;
  total: number;
  done: number;
  remaining: number;
  byProgress: Record<WorkProgressStateEnum, number>;
  overdue: number;
  unscheduled: number;
}

function buildUserSummaries(
  customers: SecureVolumeCustomerListItem[],
  persons: PersonOption[],
): UserSummary[] {
  const now = new Date();
  const avatarMap = new Map(persons.map((p) => [p.id, p.avatar]));
  const map = new Map<number, Omit<UserSummary, "remaining">>();

  for (const c of customers) {
    const userId = c.AssignedTo?.Id ?? 0;
    const userName = c.AssignedTo?.Name ?? "Ej tilldelad";

    if (!map.has(userId)) {
      map.set(userId, {
        id: userId,
        name: userName,
        avatar: avatarMap.get(userId),
        total: 0,
        done: 0,
        byProgress: {
          [WorkProgressStateEnum.NotSet]: 0,
          [WorkProgressStateEnum.New]: 0,
          [WorkProgressStateEnum.InProgress]: 0,
          [WorkProgressStateEnum.VolumePresented]: 0,
          [WorkProgressStateEnum.ContractCreated]: 0,
          [WorkProgressStateEnum.ContractSent]: 0,
          [WorkProgressStateEnum.ContractSigned]: 0,
          [WorkProgressStateEnum.Completed]: 0,
          [WorkProgressStateEnum.Submitted]: 0,
        },
        overdue: 0,
        unscheduled: 0,
      });
    }
    const s = map.get(userId)!;
    s.total++;
    s.byProgress[c.WorkProgressState]++;

    const isDone = c.WorkProgressState >= WorkProgressStateEnum.ContractSigned;
    if (isDone) {
      s.done++;
    }

    if (c.ScheduledTo && parseISO(c.ScheduledTo) < now && !isDone) {
      s.overdue++;
    }

    if (
      !c.ScheduledTo &&
      !isDone &&
      c.WorkProgressState !== WorkProgressStateEnum.NotSet
    ) {
      s.unscheduled++;
    }
  }

  return Array.from(map.values())
    .map((s) => ({ ...s, remaining: s.total - s.done }))
    .sort((a, b) => b.total - a.total);
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

const progressOrder: WorkProgressStateEnum[] = [
  WorkProgressStateEnum.NotSet,
  WorkProgressStateEnum.New,
  WorkProgressStateEnum.InProgress,
  WorkProgressStateEnum.VolumePresented,
  WorkProgressStateEnum.ContractCreated,
  WorkProgressStateEnum.ContractSent,
  WorkProgressStateEnum.ContractSigned,
  WorkProgressStateEnum.Completed,
];

/* ── Pipeline funnel bar ── */
const PipelineFunnel = ({
  byProgress,
  total,
}: {
  byProgress: Record<WorkProgressStateEnum, number>;
  total: number;
}) => {
  const theme = useTheme();

  const colorMap: Record<WorkProgressStateEnum, string> = {
    [WorkProgressStateEnum.NotSet]: theme.palette.grey[300],
    [WorkProgressStateEnum.New]: theme.palette.grey[400],
    [WorkProgressStateEnum.InProgress]: theme.palette.warning.light,
    [WorkProgressStateEnum.VolumePresented]: theme.palette.info.light,
    [WorkProgressStateEnum.ContractCreated]: theme.palette.warning.main,
    [WorkProgressStateEnum.ContractSent]: theme.palette.warning.dark,
    [WorkProgressStateEnum.ContractSigned]: theme.palette.success.light,
    [WorkProgressStateEnum.Completed]: theme.palette.success.main,
    [WorkProgressStateEnum.Submitted]: theme.palette.success.dark,
  };

  if (total === 0) return null;

  return (
    <Card elevation={0} sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Typography variant="subtitle2" mb={1.5}>
          Pipeline-fördelning
        </Typography>

        {/* Stacked bar */}
        <Box sx={{ display: "flex", height: 32, borderRadius: 2, overflow: "hidden", mb: 1.5 }}>
          {progressOrder.map((p) => {
            const count = byProgress[p];
            if (count === 0) return null;
            const pct = (count / total) * 100;
            return (
              <Tooltip key={p} title={`${progressConfig[p].label}: ${count} (${Math.round(pct)}%)`}>
                <Box
                  sx={{
                    width: `${pct}%`,
                    bgcolor: colorMap[p],
                    minWidth: count > 0 ? 4 : 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "width 0.3s ease",
                  }}
                >
                  {pct > 8 && (
                    <Typography variant="caption" sx={{ color: "white", fontWeight: 600, fontSize: "0.65rem", textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>
                      {count}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        {/* Legend */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          {progressOrder.map((p) => {
            const count = byProgress[p];
            if (count === 0) return null;
            return (
              <Stack key={p} direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: colorMap[p] }} />
                <Typography variant="caption" color="text.secondary">
                  {progressConfig[p].label} ({count})
                </Typography>
              </Stack>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

/* ── Compact card (shown for non-selected users when a selection is active) ── */
const UserCompactCard = ({
  user,
  onToggle,
}: {
  user: UserSummary;
  onToggle: () => void;
}) => {
  const percent = user.total > 0 ? Math.round((user.done / user.total) * 100) : 0;

  return (
    <Card sx={{ minWidth: 120, opacity: 0.85 }}>
      <CardActionArea onClick={onToggle}>
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar
              src={user.avatar}
              sx={{ width: 28, height: 28, fontSize: "0.7rem", bgcolor: "primary.main" }}
            >
              {getInitials(user.name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" fontWeight={600} noWrap>
                {user.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {user.done}/{user.total} ({percent}%)
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

/* ── Full card (shown when no selection, or for the selected user) ── */
const UserSummaryCard = ({
  user,
  selected,
  onToggle,
}: {
  user: UserSummary;
  selected: boolean;
  onToggle: () => void;
}) => {
  const percent = user.total > 0 ? Math.round((user.done / user.total) * 100) : 0;

  return (
    <Card
      sx={{
        minWidth: 130,
        outline: selected ? 2 : 0,
        outlineColor: "primary.main",
        outlineStyle: "solid",
      }}
    >
      <CardActionArea onClick={onToggle}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Stack alignItems="center" spacing={1}>
            <Box sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={52}
                thickness={3}
                sx={{ color: "grey.200", position: "absolute" }}
              />
              <CircularProgress
                variant="determinate"
                value={percent}
                size={52}
                thickness={3}
                sx={{
                  color:
                    percent >= 80
                      ? "success.main"
                      : percent >= 50
                        ? "warning.main"
                        : "error.main",
                }}
              />
              <Avatar
                src={user.avatar}
                sx={{
                  position: "absolute",
                  top: 6,
                  left: 6,
                  width: 40,
                  height: 40,
                  fontSize: "0.85rem",
                  bgcolor: "primary.main",
                }}
              >
                {getInitials(user.name)}
              </Avatar>
            </Box>
            <Typography variant="body2" fontWeight={600} noWrap>
              {user.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user.done}/{user.total} klara ({percent}%)
            </Typography>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={percent}
            sx={{
              mt: 1,
              height: 6,
              borderRadius: 3,
              bgcolor: "grey.200",
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                bgcolor:
                  percent >= 80
                    ? "success.main"
                    : percent >= 50
                      ? "warning.main"
                      : "error.main",
              },
            }}
          />
        </CardContent>
      </CardActionArea>
    </Card>
  );
};


const SecureVolumeDashboard = () => {
  const { data: products = [], isLoading: productsLoading } = useGetSecureVolumeProductsQuery();
  const [selectedProductNo, setSelectedProductNo] = useState<number | undefined>(undefined);
  // Visningsfilter: vilket målår listan visar. Förifylls med produktens defaultTargetYear.
  // Påverkar ALDRIG vilket år ärenden skapas för – det bestämmer backend utifrån produkten.
  const [viewYear, setViewYear] = useState<number | undefined>(undefined);

  // Välj bolagets första produkt (ursprungsprodukten) när listan laddats
  useEffect(() => {
    if (selectedProductNo === undefined && products.length > 0) {
      setSelectedProductNo(products[0].productNo);
    }
  }, [products, selectedProductNo]);

  const selectedProduct = findProduct(products, selectedProductNo);
  const defaultTargetYear = selectedProduct?.defaultTargetYear;

  useEffect(() => {
    setViewYear(defaultTargetYear);
  }, [defaultTargetYear]);

  const isViewingCurrentYear = viewYear == null || viewYear === defaultTargetYear;
  const [historyOpen, setHistoryOpen] = useState(false);
  // Stängd historik = alltid tillbaka på produktens aktuella målår
  const toggleHistory = () => {
    if (historyOpen) setViewYear(defaultTargetYear);
    setHistoryOpen(!historyOpen);
  };

  const { data, isLoading, error } = useGetSecureVolumeCustomersQuery(
    {
      productNo: selectedProductNo,
      // Skicka bara året när användaren tittar bakåt – annars gäller produktens målår
      ...(isViewingCurrentYear ? {} : { targetYear: viewYear }),
    },
    { skip: productsLoading },
  );

  // Årsvalen: produktens målår, några år bakåt, samt alla år som förekommer i svaret
  const yearOptions = useMemo(() => {
    if (defaultTargetYear == null) return [];
    const years = new Set<number>(Array.from({ length: 5 }, (_, i) => defaultTargetYear - i));
    for (const c of data ?? []) {
      if (c.TargetYear) years.add(c.TargetYear);
      for (const pw of c.PreviousWorks ?? []) years.add(pw.TargetYear);
    }
    return [...years].sort((a, b) => b - a);
  }, [defaultTargetYear, data]);
  const [populateWork] = usePopulateSecureVolumeWorkMutation();

  // Skapar/återskapar ärende för produktens målår – aldrig för det visade året
  const handlePopulate = (customerNo: number) =>
    populateWork({ customerNo, productNo: selectedProductNo });
  const { openWork } = useWorkPanel();
  const { persons } = useSalesPersons();
  const { currentUser, isAdmin, isSalesManager } = useUserRoles();
  const canViewAll = isAdmin() || isSalesManager();
  // Ärenden får bara skapas när man tittar på produktens aktuella målår
  const canPopulate = canViewAll && isViewingCurrentYear;
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  // Deadline = dagen innan förvaltningsperioden startar – kommer från produkten, väljs inte manuellt
  const deadline = useMemo(() => {
    if (!selectedProduct?.managementPeriodStart) return DEFAULT_DEADLINE;
    try {
      return subDays(parseISO(selectedProduct.managementPeriodStart), 1);
    } catch {
      return DEFAULT_DEADLINE;
    }
  }, [selectedProduct?.managementPeriodStart]);
  const theme = useTheme();
  const weeksLeft = Math.max(0, differenceInWeeks(deadline, new Date()));
  
  // Lock to current user for regular salespeople
  const effectiveUserId = canViewAll ? selectedUserId : (currentUser?.ExternalUserId ?? null);
  
  const userSummaries = useMemo(
    () => buildUserSummaries(data ?? [], persons),
    [data, persons]
  );
  
  const filteredData = useMemo(() => {
    if (!data) return [];
    if (effectiveUserId === null) return data;
    return data.filter((c) => (c.AssignedToId ?? 0) === effectiveUserId);
  }, [data, effectiveUserId]);
  console.log(canViewAll, isSalesManager(), isAdmin(), effectiveUserId, filteredData, data)

  // Aggregate stats (follows user selection)
  const stats = useMemo(() => {
    if (!filteredData.length) return null;
    const total = filteredData.length;
    const done = filteredData.filter((c) => c.WorkProgressState >= WorkProgressStateEnum.ContractSigned).length;
    const totalContracts = filteredData.reduce((sum, c) => sum + c.Contracts.length, 0);
    const overdue = filteredData.filter(
      (c) => c.ScheduledTo && parseISO(c.ScheduledTo) < new Date() && c.WorkProgressState < WorkProgressStateEnum.ContractSigned
    ).length;
    const byProgress = progressOrder.reduce(
      (acc, p) => {
        acc[p] = filteredData.filter((c) => c.WorkProgressState === p).length;
        return acc;
      },
      {} as Record<WorkProgressStateEnum, number>
    );
    return { total, done, totalContracts, overdue, byProgress };
  }, [filteredData]);

  const productControls = (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
        <SecureVolumeProductSelect
          products={products}
          value={selectedProductNo}
          onChange={setSelectedProductNo}
          disabled={productsLoading}
        />
        {selectedProduct && (
          <Tooltip title="Visa listan för ett tidigare målår. Nya ärenden skapas alltid för produktens aktuella målår.">
            <Button
              size="small"
              variant={historyOpen ? "contained" : "outlined"}
              startIcon={<HistoryIcon />}
              onClick={toggleHistory}
            >
              {historyOpen ? "Dölj historik" : "Visa historik"}
            </Button>
          </Tooltip>
        )}
        <Tooltip title="Sista dag innan förvaltningsperioden startar – hämtas från produkten">
          <Typography variant="body2" color="text.secondary">
            Deadline <strong>{deadline.toLocaleDateString("sv-SE")}</strong> ·{" "}
            <strong>{weeksLeft}</strong> veckor kvar
          </Typography>
        </Tooltip>
      </Stack>
      {selectedProduct && (
        <Typography variant="caption" color="text.secondary">
          {formatProductPeriod(selectedProduct)}
        </Typography>
      )}
      {historyOpen && (
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="text.secondary">
            Målår:
          </Typography>
          {yearOptions.map((y) => {
            const isCurrent = y === defaultTargetYear;
            const isActive = (viewYear ?? defaultTargetYear) === y;
            return (
              <Chip
                key={y}
                label={isCurrent ? `${y} (aktuellt)` : y}
                size="small"
                color={isActive ? "primary" : "default"}
                variant={isActive ? "filled" : "outlined"}
                onClick={() => setViewYear(y)}
              />
            );
          })}
        </Stack>
      )}
      {!isViewingCurrentYear && (
        <Alert severity="info" sx={{ py: 0 }}>
          Visar historisk lista för {viewYear}. Ärenden kan bara skapas för produktens
          aktuella målår ({defaultTargetYear}).
        </Alert>
      )}
    </Stack>
  );

  if (isLoading || productsLoading) {
    return (
      <Stack spacing={3}>
        {productControls}
        <Box display="flex" justifyContent="center" alignItems="center" py={6}>
          <CircularProgress />
        </Box>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack spacing={3}>
        {productControls}
        <Alert severity="error">
          Det gick inte att ladda volymsäkringsdata. Försök igen senare.
        </Alert>
      </Stack>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Stack spacing={3}>
        {productControls}
        <Typography variant="body2" color="text.secondary">
          Inga kunder med volymsäkring hittades
          {selectedProduct ? ` för ${selectedProduct.productName}` : ""}.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      {productControls}

      {/* User summary cards (only for admins/managers) */}
      {canViewAll && (
        <>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
            {userSummaries.map((user) => {
              const isSelected = selectedUserId === user.id;
              const hasSelection = selectedUserId !== null;

              if (hasSelection && !isSelected) {
                return (
                  <UserCompactCard
                    key={user.id}
                    user={user}
                    onToggle={() => setSelectedUserId(user.id)}
                  />
                );
              }

              return (
                <UserSummaryCard
                  key={user.id}
                  user={user}
                  selected={isSelected}
                  onToggle={() =>
                    setSelectedUserId((prev) => (prev === user.id ? null : user.id))
                  }
                />
              );
            })}
          </Box>

          {selectedUserId !== null && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Visar {filteredData.length} av {data.length} kunder
              </Typography>
              <Chip
                label="Rensa filter"
                size="small"
                onDelete={() => setSelectedUserId(null)}
              />
            </Stack>
          )}
        </>
      )}

      {/* Overview stat cards */}
      {stats && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
          <MiniStatCard
            title="Totalt kunder"
            value={stats.total}
            icon={<PeopleIcon />}
            color={theme.palette.primary.main}
          />
          <MiniStatCard
            title="Klara"
            value={`${stats.done} (${stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0}%)`}
            icon={<CheckCircleIcon />}
            color={theme.palette.success.main}
          />
          <MiniStatCard
            title="Antal avtal"
            value={stats.totalContracts}
            icon={<DescriptionIcon />}
            color={theme.palette.info.main}
          />
          <MiniStatCard
            title="Försenade"
            value={stats.overdue}
            icon={<SpeedIcon />}
            color={stats.overdue > 0 ? theme.palette.error.main : theme.palette.success.main}
          />
        </Box>
      )}

      {/* Pipeline */}
      {stats && <PipelineFunnel byProgress={stats.byProgress} total={stats.total} />}

      {/* Customer table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Kund</TableCell>
              <TableCell>Ärende</TableCell>
              <TableCell>Tidigare ärenden</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Aktivitet</TableCell>
              <TableCell>Nästa uppföljning</TableCell>
              <TableCell>Ansvarig</TableCell>
              <TableCell align="right">Antal avtal</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((customer) => {
              const scheduledInfo = formatSmartDate(customer.ScheduledTo);
              return (
                <TableRow key={customer.CustomerNo}>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/customer/${customer.CustomerNo}`}
                      variant="body2"
                    >
                      {customer.CustomerName ?? customer.CustomerNo}
                    </Link>
                    {customer.CustomerId && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {customer.CustomerId}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {customer.HasExistingWork && customer.WorkNo ? (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => openWork(customer.WorkNo!)}
                          sx={{ cursor: "pointer" }}
                        >
                          Ärende #{customer.WorkNo}
                        </Link>
                        {canPopulate && (
                          <Tooltip title="Återskapa ärende">
                            <IconButton
                              size="small"
                              onClick={() => handlePopulate(customer.CustomerNo)}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    ) : canPopulate ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => handlePopulate(customer.CustomerNo)}
                      >
                        Skapa ärende
                      </Button>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {(customer.PreviousWorks?.length ?? 0) > 0 ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {[...customer.PreviousWorks]
                          .sort((a, b) => b.TargetYear - a.TargetYear)
                          .map((pw) => {
                            const state = progressConfig[pw.WorkProgressState];
                            // Öppet ärende för ett passerat målår = någon har glömt avsluta det
                            const isStale =
                              pw.WorkProgressState < WorkProgressStateEnum.Completed &&
                              defaultTargetYear != null &&
                              pw.TargetYear < defaultTargetYear;
                            const tooltip = [
                              `Ärende #${pw.WorkNo} · handlar för ${pw.TargetYear}`,
                              getProductName(products, pw.ProductNo),
                              state?.label ?? "Okänd status",
                              isStale ? "Öppet ärende för passerat målår" : null,
                            ]
                              .filter(Boolean)
                              .join(" · ");
                            return (
                              <Tooltip key={pw.WorkNo} title={tooltip}>
                                <Chip
                                  icon={isStale ? <WarningAmberIcon /> : <HistoryIcon />}
                                  label={`${pw.TargetYear} · #${pw.WorkNo}`}
                                  size="small"
                                  variant="outlined"
                                  color={isStale ? "warning" : "default"}
                                  onClick={() => openWork(pw.WorkNo)}
                                  sx={{ cursor: "pointer" }}
                                />
                              </Tooltip>
                            );
                          })}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={progressConfig[customer.WorkProgressState]?.label ?? "Okänd"}
                      color={progressConfig[customer.WorkProgressState]?.color ?? "default"}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={customer.HasWorkItem ? "Har aktivitet" : "Ingen aktivitet"}>
                        <TaskAltIcon
                          fontSize="small"
                          color={customer.HasWorkItem ? "success" : "disabled"}
                        />
                      </Tooltip>
                      <Tooltip title={customer.HasFollowUp ? "Uppföljning planerad" : "Ingen uppföljning"}>
                        <FollowTheSignsIcon
                          fontSize="small"
                          color={customer.HasFollowUp ? "info" : "disabled"}
                        />
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={customer.ScheduledTo ?? ""}>
                      <Chip
                        icon={scheduledInfo.icon as React.ReactElement}
                        label={scheduledInfo.display}
                        color={
                          scheduledInfo.color as
                            | "default"
                            | "error"
                            | "success"
                            | "info"
                        }
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>{customer.AssignedTo?.Name ?? "—"}</TableCell>
                  <TableCell align="right">
                    {customer.Contracts.length}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default SecureVolumeDashboard;
